import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { uploadAttachments } from "../../lib/attachments";
import { Header } from "../../components/vc/Header";
import { UploadCard } from "../../components/vc/UploadCard";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { Input } from "../../components/ui/input";
import { Section } from "../../components/vc/layout";
import { ImageStrip } from "../../components/vc/ImageStrip";
import {
  ArrowLeft, FileSpreadsheet, Image as ImageIcon, Pencil, Sparkles, Plus, Trash2, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const UNITS = ["pcs", "kg", "ltr", "box", "bag", "meter", "gram", "dozen"];

export default function VCGenerateOrder() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("select"); // select | manual | image
  const [manualRows, setManualRows] = useState([{ product: "", quantity: "", unit: "pcs", unit_price: "" }]);
  const [excelBusy, setExcelBusy] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const goReview = (items, meta = {}) => {
    navigate("/orders/review", { state: { items, ...meta } });
  };

  const onExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExcelBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await apiClient.post("/import/parse", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const items = (data.items || []).map((it) => ({
        product: it.product,
        quantity: it.quantity,
        unit: it.unit || "pcs",
        unit_price: "",
      }));
      if (items.length === 0) { toast.error("No items found in file"); return; }
      toast.success(`${items.length} items extracted`);
      goReview(items);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Excel parse failed");
    } finally {
      setExcelBusy(false);
      e.target.value = "";
    }
  };

  const onImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const { images } = await uploadAttachments(files, { existingCount: attachments.length });
      if (images.length === 0) return;
      const merged = [...attachments, ...images].slice(0, 5);
      setAttachments(merged);
      setImagePreview(merged[0] || "");
      setMode("image");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submitImage = () => {
    // Image-only order: attach image(s), no OCR — buyer specifies items on review screen
    goReview(
      [{ product: "As per attached image", quantity: 1, unit: "pcs", unit_price: "" }],
      { image_data: attachments[0] || imagePreview || "", images: attachments },
    );
  };

  const submitManual = () => {
    const items = manualRows
      .filter((r) => r.product.trim() && Number(r.quantity) > 0)
      .map((r) => ({
        product: r.product.trim(),
        quantity: Number(r.quantity),
        unit: r.unit || "pcs",
        unit_price: Number(r.unit_price || 0),
      }));
    if (items.length === 0) { toast.error("Add at least one valid item"); return; }
    goReview(items);
  };

  const updateRow = (i, k, v) => setManualRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeRow = (i) => setManualRows((r) => r.filter((_, idx) => idx !== i));
  const addRow = () => setManualRows((r) => [...r, { product: "", quantity: "", unit: "pcs", unit_price: "" }]);

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-40 space-y-5">
        <button onClick={() => navigate(-1)} data-testid="generate-order-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div>
          <div className="inline-flex items-center gap-1.5 text-emerald-600 text-[10px] font-bold uppercase tracking-widest mb-1">
            <Sparkles className="w-3 h-3" strokeWidth={2.6} /> New Order
          </div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Generate Order</h2>
          <p className="text-sm text-slate-500 mt-0.5">Pick a method and move to review</p>
        </div>

        <div className="space-y-2.5">
          <UploadCard
            title="Upload Excel"
            subtitle="XLSX or CSV · bulk create items"
            Icon={FileSpreadsheet}
            tone="emerald"
            accept=".xlsx,.csv"
            onChange={onExcel}
            testid="method-order-excel"
          />
          <UploadCard
            title={uploading ? "Uploading..." : "Upload Image / PDF"}
            subtitle="Up to 5 images or PDFs · pages become images"
            Icon={ImageIcon}
            tone="indigo"
            accept="image/*,application/pdf,.pdf"
            multiple
            onChange={onImage}
            testid="method-order-image"
          />
          <UploadCard
            title="Manual Entry"
            subtitle="Type products one by one"
            Icon={Pencil}
            tone="amber"
            onClick={() => setMode(mode === "manual" ? "select" : "manual")}
            testid="method-order-manual"
          />
        </div>

        {excelBusy && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-800 font-semibold">
            Parsing your Excel...
          </div>
        )}

        {/* Manual entry */}
        {mode === "manual" && (
          <Section title="Items" subtitle="Add products for this order" testid="gen-manual-section">
            <div className="space-y-2">
              {manualRows.map((row, i) => (
                <div key={i} data-testid={`gen-manual-row-${i}`} className="bg-white border border-slate-100 rounded-2xl p-2.5 shadow-sm">
                  {/* Compact single row: SN · product · qty · unit · unit price · delete */}
                  <div className="flex items-center gap-1.5">
                    <span className="w-6 shrink-0 text-center text-[11px] font-bold text-slate-400 tabular-nums">{i + 1}.</span>
                    <Input data-testid={`gen-manual-product-${i}`} value={row.product} onChange={(e) => updateRow(i, "product", e.target.value)} placeholder="Product" className="h-10 rounded-lg flex-1 min-w-0 text-sm" />
                    <Input data-testid={`gen-manual-qty-${i}`} type="number" min="0" step="0.01" value={row.quantity} onChange={(e) => updateRow(i, "quantity", e.target.value)} placeholder="Qty" className="h-10 rounded-lg w-14 text-sm text-right tabular-nums" />
                    <select data-testid={`gen-manual-unit-${i}`} value={row.unit} onChange={(e) => updateRow(i, "unit", e.target.value)} className="h-10 rounded-lg border border-slate-200 px-1.5 text-sm bg-white w-16">
                      {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <Input data-testid={`gen-manual-unitprice-${i}`} type="number" min="0" step="0.01" inputMode="decimal" value={row.unit_price} onChange={(e) => updateRow(i, "unit_price", e.target.value)} placeholder="Rate" className="no-spinner h-10 rounded-lg w-16 text-sm text-right tabular-nums" />
                    <button onClick={() => removeRow(i)} data-testid={`gen-manual-delete-${i}`} className="w-9 h-10 shrink-0 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 grid place-items-center transition" aria-label="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              <SecondaryButton onClick={addRow} data-testid="gen-manual-add-row" className="w-full">
                <Plus className="w-4 h-4 inline mr-1" /> Add Another Item
              </SecondaryButton>
            </div>
          </Section>
        )}

        {/* Image / PDF preview — strip of thumbnails */}
        {mode === "image" && attachments.length > 0 && (
          <Section title="Attached images" subtitle={`${attachments.length}/5 pages`} testid="gen-image-section">
            <div className="bg-white rounded-2xl border border-slate-100 p-3 space-y-3">
              <ImageStrip
                images={attachments}
                onRemove={(i) => {
                  const next = attachments.filter((_, idx) => idx !== i);
                  setAttachments(next);
                  setImagePreview(next[0] || "");
                  if (next.length === 0) setMode("select");
                }}
                testid="gen-image-strip"
              />
              <div className="grid grid-cols-2 gap-2">
                <label
                  htmlFor="gen-reupload"
                  className={`h-11 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-700 font-semibold text-sm grid place-items-center cursor-pointer transition ${attachments.length >= 5 ? "opacity-40 pointer-events-none" : ""}`}
                >
                  {attachments.length >= 5 ? "Limit reached" : "Add more"}
                </label>
                <input id="gen-reupload" type="file" accept="image/*,application/pdf,.pdf" multiple onChange={onImage} className="hidden" data-testid="gen-image-add-more" />
                <button
                  onClick={() => { setAttachments([]); setImagePreview(""); setMode("select"); }}
                  data-testid="gen-image-remove"
                  className="h-11 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 font-semibold text-sm transition"
                >
                  Remove all
                </button>
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-xl p-3">
                Attached pages travel with the order (Max 5). PDFs are auto-converted to images.
              </div>
            </div>
          </Section>
        )}

        {(mode === "manual" || mode === "image") && (
          <PrimaryButton
            onClick={mode === "manual" ? submitManual : submitImage}
            data-testid="review-order-btn"
            className="w-full h-14 flex items-center justify-center gap-2 text-base"
          >
            Review Order &amp; Place <ArrowRight className="w-5 h-5" />
          </PrimaryButton>
        )}
      </div>
    </>
  );
}
